import { MixpanelProvider as provider } from './providers/mixpanel.provider';
import { ANALYTICS_EVENTS } from '../../constants/analyticsEvents.constants';

// ── Re-export catalog ─────────────────────────────────────────────────────────
export { ANALYTICS_EVENTS };


export default {
  // ── Generic ───────────────────────────────────────────────────

  // The named wrappers below predate this and stay as they are — live
  // Mixpanel boards are built on the exact event names they send, so they
  // are not worth churning. New instrumentation passes an ANALYTICS_EVENTS
  // constant straight through here instead of growing one more wrapper per
  // event; most callers reach it via the useAnalytics hook (Hooks/
  // useAnalytics.js), which fills in `from`/`email` for them.
  track : (eventName, properties = {}) => provider.track(eventName, properties),

  // ── Auth ──────────────────────────────────────────────────────────────────────

  identifyUser : ({ email, name, role }) =>
    provider.identify(email, { $name: name, $email: email, role }),


  trackLogout : () => {
    // IMPORTANT: track fires BEFORE reset.
    // mixpanel.track() is synchronous (buffers to a queue internally),
    // so the event is captured before reset() wipes the user identity.
    provider.track(ANALYTICS_EVENTS.AUTH.USER_LOGGED_OUT);
    provider.reset();
  },

  // ── Jobs Library ──────────────────────────────────────────────────────────────

  trackSearchSelectedJds : ({ from, email, searchSelectedJd, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.SEARCH_SELECTED_JDS, { from, email , searchSelectedJd, description}),

  trackSearchJds : ({ from, email, searchJD, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.SEARCH_JDS, { from, email , searchJD, description}),

  trackJdStatusFilter : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JD_STATUS_FILTER, { from, email , description}),

  trackCriticalStatusFilter : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.CRITICAL_STATUS_FILTER, { from, email , description}),

  trackCompareJDs : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.COMPARE_JDS, { from, email , description}),

  trackChangeToggle : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.CHANGE_TOGGLE, { from, email , description}),

  trackToggleFilter : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.TOGGLE_FILTER, { from, email , description}),

  trackCreateJD : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.CREATE_JD, { from, email , description}),

  trackCreateJDSubmit : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.CREATE_JD_SUBMIT, { from, email , description}),

  trackReviseJDSubmit : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.REVISE_JD_SUBMIT, { from, email , description}),

  trackClearAllFilters : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.CLEAR_ALL_FILTERS, { from, email , description}),

  trackCloseDrawer : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.CLOSE_DRAWER, { from, email , description}),

  // ── Jobs Details ──────────────────────────────────────────────────────────────

  trackDifferenceChecker : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.DIFFERENCE_CHECKER, { from, email , description}),

  trackEditJD : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.EDIT_JD, { from, email , description}),

  trackReviseJD : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.REVISE_JD, { from, email , description}),

  trackDownloadJD : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.DOWNLOAD_JD, { from, email , description}),

  trackSubmitForApproval : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.SUBMIT_FOR_APPROVAL, { from, email , description}),

  trackUnPublish : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.UNPUBLISH_JD, { from, email , description}),

  trackDownloadPDF : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.DOWNLOAD_PDF, { from, email , description}),

  trackDownloadDOCX : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.DOWNLOAD_DOCX, { from, email , description}),

  trackVersionChange : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.VERSION_CHANGE, { from, email , description}),

  trackSelectedJdTab : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.SELECTED_JD_TAB, { from, email , description}),

  trackSaveClick : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.SAVE_CLICK, { from, email , description}),

  trackApproverSubmitJD : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.APPROVER_SUBMIT_JD, { from, email , description}),

  trackApprovedJD : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.APPROVED_JD, { from, email , description}),

  trackCloseJobDetailsPage : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.CLOSE_JOB_DETAILS_PAGE, { from, email , description}),

  trackCustomCompare : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.CUSTOM_COMPARE, { from, email , description}),

  trackComparJobs : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.COMPARE_JOBS, { from, email , description}),

  trackComparJobsChecker : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.COMPARE_JOBS_DIFFERENCE_CHECKER, { from, email , description}),

  trackCompareMarkdown : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.JOBS_LIBRARY.JOB_DETAILS.COMPARE_MARKDOWN, { from, email , description}),

  // ── People+AI ─────────────────────────────────────────────────────────────

  // Main landing page
  trackPAICRefreshData : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.REFRESH_DATA, { from, email, description }),

  trackPAICSearchRoles : ({ from, email, searchTerm, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.SEARCH_ROLES, { from, email, searchTerm, description }),

  trackPAICFilterRoles : ({ from, email, filterType, filterValue, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.FILTER_ROLES, { from, email, filterType, filterValue, description }),

  trackPAICClearFilters : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.CLEAR_FILTERS, { from, email, description }),

  trackPAICCreateGroup : ({ from, email, groupName, rolesCount, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.CREATE_GROUP, { from, email, groupName, rolesCount, description }),

  trackPAICSelectGroup : ({ from, email, groupName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.SELECT_GROUP, { from, email, groupName, description }),

  trackPAICEvaluateInitiated : ({ from, email, groupName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.EVALUATE_PAIC_INITIATED, { from, email, groupName, description }),

  trackPAICReEvaluateInitiated : ({ from, email, groupName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.RE_EVALUATE_PAIC_INITIATED, { from, email, groupName, description }),

  trackPAICViewPotential : ({ from, email, groupName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.VIEW_PAIC_POTENTIAL, { from, email, groupName, description }),

  trackPAICPaginationChange : ({ from, email, page, itemsPerPage, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.PAGINATION_CHANGE, { from, email, page, itemsPerPage, description }),

  // Group Agents View
  trackPAICBackToMain : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.BACK_TO_MAIN, { from, email, description }),

  trackPAICViewByToggle : ({ from, email, viewMode, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.VIEW_BY_TOGGLE, { from, email, viewMode, description }),

  trackPAICSelectAgent : ({ from, email, agentId, agentName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.SELECT_AGENT, { from, email, agentId, agentName, description }),

  trackPAICDeselectAgent : ({ from, email, agentId, agentName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.DESELECT_AGENT, { from, email, agentId, agentName, description }),

  trackPAICSelectAllAgents : ({ from, email, selected, agentCount, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.SELECT_ALL_AGENTS, { from, email, selected, agentCount, description }),

  trackPAICSubmitAgents : ({ from, email, agentCount, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.SUBMIT_AGENTS, { from, email, agentCount, description }),

  trackPAICVersionSelected : ({ from, email, version, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.VERSION_SELECTED, { from, email, version, description }),

  trackPAICDocumentTypeSelected : ({ from, email, documentType, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.DOCUMENT_TYPE_SELECTED, { from, email, documentType, description }),

  trackPAICSortAgents : ({ from, email, sortColumn, sortOrder, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.SORT_AGENTS, { from, email, sortColumn, sortOrder, description }),

  trackPAICSortRoles : ({ from, email, sortColumn, sortOrder, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.SORT_ROLES, { from, email, sortColumn, sortOrder, description }),

  trackPAICAgentRowClicked : ({ from, email, agentId, agentName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.AGENT_ROW_CLICKED, { from, email, agentId, agentName, description }),

  trackPAICRoleRowClicked : ({ from, email, roleId, roleName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.ROLE_ROW_CLICKED, { from, email, roleId, roleName, description }),

  trackPAICTabChanged : ({ from, email, previousTab, newTab, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.TAB_CHANGED, { from, email, previousTab, newTab, description }),

  // Agent Details
  trackPAICAgentDetailsViewed : ({ from, email, agentId, agentName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.AGENT_DETAILS_VIEWED, { from, email, agentId, agentName, description }),

  trackPAICEditAgentName : ({ from, email, agentId, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.EDIT_AGENT_NAME, { from, email, agentId, description }),

  trackPAICSaveAgentName : ({ from, email, agentId, oldName, newName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.SAVE_AGENT_NAME, { from, email, agentId, oldName, newName, description }),

  trackPAICCancelAgentNameEdit : ({ from, email, agentId, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.CANCEL_AGENT_NAME_EDIT, { from, email, agentId, description }),

  trackPAICEditAgentDescription : ({ from, email, agentId, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.EDIT_AGENT_DESCRIPTION, { from, email, agentId, description }),

  trackPAICSaveAgentDescription : ({ from, email, agentId, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.SAVE_AGENT_DESCRIPTION, { from, email, agentId, description }),

  trackPAICCancelAgentDescriptionEdit : ({ from, email, agentId, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.CANCEL_AGENT_DESCRIPTION_EDIT, { from, email, agentId, description }),

  trackPAICExpandRoleDetail : ({ from, email, roleIndex, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.EXPAND_ROLE_DETAIL, { from, email, roleIndex, description }),

  trackPAICCollapseRoleDetail : ({ from, email, roleIndex, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.COLLAPSE_ROLE_DETAIL, { from, email, roleIndex, description }),

  // Role Impact
  trackPAICRoleImpactViewed : ({ from, email, roleId, roleName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.ROLE_IMPACT_VIEWED, { from, email, roleId, roleName, description }),

  trackPAICViewMoreRoleImpact : ({ from, email, roleId, roleName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.VIEW_MORE_ROLE_IMPACT, { from, email, roleId, roleName, description }),

  // Role Impact Details
  trackPAICRoleImpactDetailsViewed : ({ from, email, roleId, roleName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.ROLE_IMPACT_DETAILS_VIEWED, { from, email, roleId, roleName, description }),

  trackPAICExpandTaskWorkflow : ({ from, email, taskIndex, taskName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.EXPAND_TASK_WORKFLOW, { from, email, taskIndex, taskName, description }),

  trackPAICCollapseTaskWorkflow : ({ from, email, taskIndex, taskName, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.COLLAPSE_TASK_WORKFLOW, { from, email, taskIndex, taskName, description }),

  // Documents
  trackPAICJDPRDViewOpened : ({ from, email, documentType, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.JD_PRD_VIEW_OPENED, { from, email, documentType, description }),

  trackPAICJDPRDEditMode : ({ from, email, documentType, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.JD_PRD_EDIT_MODE, { from, email, documentType, description }),

  trackPAICJDPRDSave : ({ from, email, documentType, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.JD_PRD_SAVE, { from, email, documentType, description }),

  trackPAICJDPRDCancel : ({ from, email, documentType, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.JD_PRD_CANCEL, { from, email, documentType, description }),

  trackPAICJDPRDPagination : ({ from, email, direction, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.JD_PRD_PAGINATION, { from, email, direction, description }),

  trackPAICPeopleJDViewOpened : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.PEOPLE_JD_VIEW_OPENED, { from, email, description }),

  trackPAICPeopleJDEditMode : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.PEOPLE_JD_EDIT_MODE, { from, email, description }),

  trackPAICPeopleJDSave : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.PEOPLE_JD_SAVE, { from, email, description }),

  trackPAICPeopleJDCancel : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.PEOPLE_JD_CANCEL, { from, email, description }),

  trackPAICPeopleJDPagination : ({ from, email, direction, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.PEOPLE_JD_PAGINATION, { from, email, direction, description }),

  // Close actions
  trackPAICCloseAgentDetails : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.CLOSE_AGENT_DETAILS, { from, email, description }),

  trackPAICCloseRoleImpact : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.CLOSE_ROLE_IMPACT, { from, email, description }),

  trackPAICCloseRoleImpactDetails : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.CLOSE_ROLE_IMPACT_DETAILS, { from, email, description }),

  trackPAICCloseDocuments : ({ from, email, description = ""}) =>
    provider.track(ANALYTICS_EVENTS.PEOPLE_PLUS_AI.CLOSE_DOCUMENTS, { from, email, description })
}
